<template>
  <section class="ren-decoration-cp ren-decoration-cp-data-list" :class="{ 'is-compact': compact }" :style="listStyle">
    <div v-if="dataState === 'loading'" class="ren-decoration-cp-data-list__state">加载中...</div>
    <div v-else-if="dataState === 'error'" class="ren-decoration-cp-data-list__state">{{ errorText }}</div>
    <div v-else-if="items.length === 0" class="ren-decoration-cp-data-list__state">{{ emptyText }}</div>
    <article v-for="(item, index) in items" v-else :key="itemKey(item, index)">
      <img v-if="itemImage(item)" :src="itemImage(item)" :alt="itemTitle(item)" />
      <div>
        <strong :style="textStyle">{{ itemTitle(item) }}</strong>
        <span>{{ itemSubtitle(item) }}</span>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  previewBoundString,
  previewDataRecord,
  previewRecordsFor,
  recordList,
  stringValue
} from '../../../previews/preview-helpers'
import { widgetMediaUrl } from '../../media'
import type { WidgetPreviewProps } from '../../types'

const props = defineProps<WidgetPreviewProps>()

const compact = computed(() => String(props.component.props?.layoutMode ?? 'LIST').toUpperCase() === 'COMPACT')
const dataRecord = computed(() => previewDataRecord(props.componentData))
const dataState = computed(() => stringValue(dataRecord.value.status).toLowerCase())
const errorText = computed(() => stringValue(dataRecord.value.error, '加载失败'))
const emptyText = computed(() => stringValue(props.component.props?.emptyText, '暂无数据'))
const pageSize = computed(() => {
  const value = Number(props.component.props?.pageSize)
  return Number.isFinite(value) && value > 0 ? Math.min(20, Math.round(value)) : 8
})
const items = computed(() =>
  previewRecordsFor(props.component, props.componentData, recordList(props.component.props?.items)).slice(
    0,
    pageSize.value
  )
)
const listStyle = computed(() => {
  const gap = Number(props.component.style?.gap)
  const divider = stringValue(props.component.style?.dividerColor)
  return {
    gap: Number.isFinite(gap) && gap >= 0 ? `${gap}px` : undefined,
    '--list-divider': divider || undefined
  }
})
const textStyle = computed(() => {
  const color = stringValue(props.component.style?.textColor)
  return color ? { color } : undefined
})

function itemTitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.title',
    stringValue(item.title ?? item.label ?? item.name, '列表内容'),
    item
  )
}

function itemSubtitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.subtitle',
    stringValue(item.subtitle ?? item.summary ?? item.meta),
    item
  )
}

function itemImage(item: Record<string, unknown>) {
  return widgetMediaUrl(
    previewBoundString(
      props.component,
      props.componentData,
      'item.imageUrl',
      stringValue(item.imageUrl ?? item.coverUrl),
      item
    )
  )
}

function itemKey(item: Record<string, unknown>, index: number) {
  return stringValue(item.id ?? item.code, `${itemTitle(item)}-${index}`)
}
</script>

<style scoped>
.ren-decoration-cp-data-list {
  display: grid;
  min-height: 96px;
  gap: 8px;
}

.ren-decoration-cp-data-list article {
  display: flex;
  gap: 12px;
  min-width: 0;
  padding: 12px;
  border-bottom: 1px solid var(--list-divider, var(--ren-theme-border));
}

.ren-decoration-cp-data-list.is-compact article {
  padding: 8px;
}

.ren-decoration-cp-data-list img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
}

.ren-decoration-cp-data-list article div {
  min-width: 0;
  display: grid;
  align-content: center;
  gap: 4px;
}

.ren-decoration-cp-data-list span {
  color: var(--ren-theme-muted);
  font-size: 12px;
  overflow-wrap: anywhere;
}

.ren-decoration-cp-data-list strong {
  overflow-wrap: anywhere;
}

.ren-decoration-cp-data-list__state {
  min-height: 96px;
  display: grid;
  place-items: center;
  color: var(--ren-theme-muted);
  background: var(--ren-theme-background);
}
</style>
